<template>
  <!-- w-full so the field fills its container on its own; with inheritAttrs:false
       a parent's w-full lands on the inner control, not this root. See BaseInput. -->
  <div class="w-full" v-bind="rootAttrs">
    <FloatLabel variant="on" class="relative transition-colors duration-200 !font-just w-full block">
      <MultiSelect
        v-bind="controlAttrs"
        :modelValue="internalValue"
        :options="options"
        :optionLabel="optionLabel"
        :optionValue="optionValue"
        :placeholder="placeholder"
        :disabled="disabled"
        :id="id"
        :name="name"
        :data-test-id="`${id}-multiselect`"
        @update:modelValue="handleChange"
        @focus="handleFocus"
        @blur="handleBlur"
        :maxSelectedLabels="computedMaxSelectedLabels"
        :selectedItemsLabel="selectedItemsLabel"
        :selectionLimit="selectionLimit"
        class="bg-transparent !border-[2px] !rounded-[8px] !px-[14px] !py-[8.5px] !text-labelColor !font-just !text-base"
        :class="[inputClasses, width]"
        :showClear="false"
        :pt="{
          input: { class: 'text-left w-full' },
          label: { class: '!text-left !w-full' },
          item: { class: '!text-left !font-just' },
          panel: { class: '!text-left !font-just' },
          filterInput: { class: '!text-left' },
          token: { class: '!font-just' },
          tokenLabel: { class: '!font-just' },
          headerCheckbox: { class: 'hidden' }
        }"
        :showToggleAll="showToggleAll"
      >
        <template #dropdownicon>
          <span
            v-html="dropdownCaret"
            class="dropdown-icon flex items-center !font-just"
          ></span>
        </template>
        
      </MultiSelect>

      <label
        :for="id"
        class="absolute left-2 -top-2 !font-just !text-base !font-400 pointer-events-none transition-all duration-200 flex gap-1"
        :class="labelClasses"
        :data-test-id="`${id}-label`"
      >
        <template v-if="required">
          <span v-if="focused || hasValue">*</span>{{ label
          }}<span v-if="!focused && !hasValue">*</span>
        </template>
        <template v-else>
          {{ label }}
        </template>
      </label>
    </FloatLabel>

    <Message
      v-if="invalid && errorMessage"
      severity="error"
      size="small"
      variant="simple"
      :class="{
        '!text-error  !px-[10px] !mt-[3.84px] !font-robo !font-400 !text-[15.36px]':
          invalid,
      }"
      :data-test-id="`${id}-errorMessage`"
    >
      {{ errorMessage }}
    </Message>
  </div>
</template>

<script setup>
// Forward fallthrough attrs (tabindex, @focus/@click) onto the inner control via
// v-bind="$attrs", not the wrapper <div>, so a parent-supplied tabindex (e.g.
// from CustomField) doesn't create a second tab stop. See BaseInput.vue.
defineOptions({ inheritAttrs: false });

import { ref, computed, watch } from "vue";
import { useControlAttrs } from "@/composables/useControlAttrs";

// class/style -> root (grid col-span, width); the rest -> inner control.
const { rootAttrs, controlAttrs } = useControlAttrs();
import { dropdownCaret } from "@/utils/commonSvg";
import { MultiSelect } from "primevue";

const props = defineProps({
  modelValue: {
    type: Array,
    default: () => []
  },
  label: String,
  id: String,
  name: {
    type: String,
    default: "multiselect",
  },
  options: {
    type: Array,
    default: () => [],
  },
  optionLabel: {
    type: String,
    default: "label",
  },
  optionValue: {
    type: String,
    default: "value",
  },
  placeholder: {
    type: String,
    default: "",
  },
  required: Boolean,
  errorMessage: String,
  invalid: Boolean,
  width: { type: String, default: "w-full" },
  disabled: { type: Boolean, default: false },
  showClear: { type: Boolean, default: false },
  maxSelectedLabels: {
    type: Number,
    default: null
  },
  selectedItemsLabel: {
    type: String,
    default: "{0} items selected"
  },
  selectionLimit: {
    type: Number,
    default: null
  },
  showChips: {
    type: Boolean,
    default: false
  },
  showToggleAll: {
    type: Boolean,
    default: false
  }
});

const emit = defineEmits(["update:modelValue", "change", "blur"]);
const focused = ref(false);
const internalValue = ref(props.modelValue || []);

watch(
  () => props.modelValue,
  (val) => {
    const newVal = val || [];
    if (JSON.stringify(newVal) !== JSON.stringify(internalValue.value)) {
      internalValue.value = newVal;
    }
  }
);

const handleChange = (value) => {
  const newValue = value || [];
  internalValue.value = newValue;
  emit("update:modelValue", newValue);
  emit("change", newValue);
};

const handleFocus = () => {
  focused.value = true;
};

const handleBlur = (event) => {
  focused.value = false;
  emit("blur", event);
};

const hasValue = computed(() => {
  return internalValue.value && internalValue.value.length > 0;
});

const computedMaxSelectedLabels = computed(() => {
  if (props.maxSelectedLabels !== null && props.maxSelectedLabels !== undefined) {
    return props.maxSelectedLabels;
  }

  const optionCount = Array.isArray(props.options) ? props.options.length : 0;
  return optionCount > 0 ? optionCount : 999;
});

const inputClasses = computed(() => ({
  "!border-deepSky": (focused.value || hasValue.value) && !props.invalid,
  "!border-softGray": !focused.value && !hasValue.value && !props.invalid,
  "!border-error": props.invalid,
}));

const labelClasses = computed(() => ({
  "!text-deepSky": (focused.value || hasValue.value) && !props.invalid,
  "!text-greyBlue": !focused.value && !hasValue.value && !props.invalid,
  "!text-error": props.invalid,
}));

</script>